import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    MapPin,
    BedDouble,
    Bath,
    Maximize,
    Home,
    Car,
    Sofa,
    Compass,
    Building2,
    Calendar,
    Phone,
    Mail,
    Share2,
    Heart,
    ArrowLeft,
    CheckCircle2,
    Layers,
    X,
    MessageCircle,
    Star,
    MapPinned,
    Route,
    TreePine,
    FileText,
    Sparkles,
    Info
} from "lucide-react";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import RealEstateNavbar from "../../../components/realestateagents/RealEstateNavbar";

import toast from "react-hot-toast";

const HousingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);

    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [enquiryForm, setEnquiryForm] = useState({
        name: "",
        email: "",
        mobile: "",
        message: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const response = await axiosInstancev1.get(`/properties/${id}`);
                console.log("Property Fetch Response:", response.data);

                // The backend returns: { success: true, ...listing, details, images }
                // Handle both wrapped and unwrapped responses
                const listingData = response.data.success !== undefined
                    ? { ...response.data } // Remove success flag but keep all data
                    : response.data;

                // Remove success flag if present
                const { success, ...propertyData } = listingData;

                // Helper function to parse JSON fields safely
                const parseJsonField = (field) => {
                    if (!field) return [];
                    if (Array.isArray(field)) return field;
                    if (typeof field === 'string') {
                        try {
                            const parsed = JSON.parse(field);
                            return Array.isArray(parsed) ? parsed : [parsed];
                        } catch (e) {
                            // If it's a plain string, split by comma or return as single item
                            return field.includes(',') ? field.split(',').map(s => s.trim()) : [field];
                        }
                    }
                    return [];
                };

                // Merge listing data with details
                const mergedProperty = {
                    ...propertyData,
                    // Map housing details to property
                    property_title: listingData.property_title || listingData.details?.property_type || listingData.plot_title || "Untitled Property",
                    expected_price: listingData.details?.expected_price || listingData.expected_price,
                    bedrooms: listingData.details?.bedrooms || 0,
                    bathrooms: listingData.details?.bathrooms || 0,
                    built_up_area: listingData.details?.built_up_area || listingData.details?.builtupArea || 0,
                    plot_area: listingData.details?.plot_area || listingData.details?.plotArea || 0,
                    furnishing_status: listingData.details?.furnishing_status || listingData.details?.furnishing || "N/A",
                    facing_direction: listingData.details?.facing_direction || listingData.details?.facing || "N/A",
                    floor_number: listingData.details?.floor_number || listingData.details?.floorNumber || 0,
                    total_floors: listingData.details?.total_floors || listingData.details?.totalFloors || 0,
                    car_parking_slots: listingData.details?.car_parking_slots || listingData.details?.carParking || 0,
                    property_type: listingData.details?.property_type || listingData.property_type || "Apartment",
                    description: listingData.details?.description || "",
                    amenities: parseJsonField(listingData.details?.amenities),
                    // NEW FIELDS - Housing specific
                    highlights: listingData.details?.highlights || "",
                    nearby_locations: listingData.details?.nearby_locations || "",
                    // NEW FIELDS - Plot specific
                    nearby_facilities: parseJsonField(listingData.details?.nearby_facilities),
                    advantages: listingData.details?.advantages || "",
                    distance_to_main_road: listingData.details?.distance_to_main_road || "",
                    soil_type: listingData.details?.soil_type || "",
                    survey_number: listingData.details?.survey_number || "",
                    plot_title: listingData.details?.plot_title || listingData.plot_title || "",
                    // Images
                    images: listingData.images || [],
                };

                setProperty(mergedProperty);
            } catch (error) {
                console.error("Failed to load property", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProperty();
    }, [id]);

    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        if (!enquiryForm.name || !enquiryForm.mobile || !enquiryForm.message) {
            toast.error("Please fill in all required fields: Name, Mobile, and Message.");
            return;
        }

        // Validate mobile number (basic validation)
        if (enquiryForm.mobile.length < 10) {
            toast.error("Please enter a valid mobile number.");
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await axiosInstancev1.post(`/properties/${id}/enquire`, enquiryForm);
            if (response.data.success) {
                toast.success("Your enquiry has been sent successfully! The agent will contact you soon.");
                setShowEnquiryModal(false);
                setEnquiryForm({ name: "", email: "", mobile: "", message: "" });
            }
        } catch (error) {
            console.error("Enquiry submission failed", error);
            toast.error(error.response?.data?.message || "Failed to send enquiry. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!property) {
        return <div className="p-8 text-center text-gray-500">Property not found</div>;
    }

    const images = property.images?.length > 0
        ? property.images.map(img => ({
            image_url: img.image_url || img.url || "https://via.placeholder.com/800x400?text=No+Image"
        }))
        : [{ image_url: "https://via.placeholder.com/800x400?text=No+Image" }];

    const details = property.details || property;

    return (
        <div className="min-h-screen bg-white font-sans pb-10">
            <RealEstateNavbar forceWhite={true} />

            <div className="pt-24 max-w-7xl mx-auto px-4">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start mb-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                            {property.property_title || property.details?.property_type || "Premium Property"}
                        </h1>
                        <div className="flex items-center gap-2 text-gray-500 mt-2">
                            <MapPin size={18} />
                            <span className="text-sm md:text-base">
                                {[property.street_address, property.locality, property.city].filter(Boolean).join(", ") || "Location not specified"}
                            </span>
                        </div>
                    </div>
                    <div className="mt-4 md:mt-0 text-left md:text-right">
                        <div className="text-3xl font-bold text-gray-900">
                            ₹ {property.expected_price || details?.expected_price || property.price || details?.price
                                ? (Number(property.expected_price || details?.expected_price || property.price || details?.price) / 100000).toFixed(2) + " Lac"
                                : "Price on Request"}
                        </div>
                        {property.expected_price && property.built_up_area && (
                            <div className="text-sm text-gray-500 mt-1">
                                @ ₹ {Math.round(Number(property.expected_price) / Number(property.built_up_area || 1))} per sq.ft
                            </div>
                        )}
                    </div>
                </div>

                {/* Gallery Section */}
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-2 md:gap-3 mb-8 h-[300px] md:h-[450px] lg:h-[550px]">
                    {/* Main Image */}
                    <div className="md:col-span-3 h-full relative rounded-2xl overflow-hidden group cursor-pointer" onClick={() => setShowLightbox(true)}>
                        <div className="absolute inset-0 bg-gray-200 animate-pulse -z-10" />
                        <img
                            src={images[activeImage].image_url}
                            alt="Property Main"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60" />
                        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-md text-gray-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                            <Maximize size={16} />
                            View {images.length} Photos
                        </div>
                    </div>

                    {/* Side Grid - Desktop only */}
                    <div className="hidden md:grid grid-rows-2 gap-2 md:gap-3 h-full">
                        {images.slice(1, 3).map((img, idx) => (
                            <div
                                key={idx}
                                className="rounded-2xl overflow-hidden h-full cursor-pointer relative group"
                                onClick={() => {
                                    setActiveImage(idx + 1);
                                }}
                            >
                                <img
                                    src={img.image_url}
                                    alt="Thumbnail"
                                    className="w-full h-full object-cover hover:opacity-90 transition-all duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
                            </div>
                        ))}
                        {images.length < 2 && (
                            <div className="bg-gray-100 rounded-2xl flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                                <Building2 size={32} className="mb-2 opacity-50" />
                                <span className="text-xs">No image</span>
                            </div>
                        )}
                        {images.length < 3 && images.length >= 1 && (
                            <div className="bg-gray-100 rounded-2xl flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                                <Building2 size={32} className="mb-2 opacity-50" />
                                <span className="text-xs">No image</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Thumbnails Navigation (All Screens) */}
                <div className="flex gap-3 overflow-x-auto pb-4 mb-8 scrollbar-hide no-scrollbar">
                    {images.map((img, idx) => (
                        <div
                            key={idx}
                            onClick={() => setActiveImage(idx)}
                            className={`relative min-w-[100px] h-20 md:min-w-[120px] md:h-24 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 flex-shrink-0 ${activeImage === idx
                                ? 'ring-2 ring-blue-600 ring-offset-2'
                                : 'opacity-60 hover:opacity-100 hover:scale-105'
                                }`}
                        >
                            <img
                                src={img.image_url}
                                className="w-full h-full object-cover"
                                alt={`Thumbnail ${idx + 1}`}
                            />
                        </div>
                    ))}
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Key Specs Card */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-semibold mb-4">Property Overview</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                {property.property_category === 'housing' ? (
                                    <>
                                        <SpecItem icon={BedDouble} label="Bedrooms" value={property.bedrooms || details?.bedrooms || "N/A"} />
                                        <SpecItem icon={Bath} label="Bathrooms" value={property.bathrooms || details?.bathrooms || "N/A"} />
                                        <SpecItem icon={Maximize} label="Area" value={`${property.built_up_area || property.plot_area || details?.built_up_area || details?.plot_area || details?.builtupArea || details?.plotArea || 0} sq.ft`} />
                                        <SpecItem icon={Sofa} label="Furnishing" value={property.furnishing_status || details?.furnishing_status || details?.furnishing || "N/A"} />
                                        <SpecItem icon={Compass} label="Facing" value={property.facing_direction || details?.facing_direction || details?.facing || "N/A"} />
                                        <SpecItem icon={Building2} label="Floor" value={property.floor_number ? `${property.floor_number} / ${property.total_floors}` : details?.floor_number ? `${details.floor_number} / ${details.total_floors}` : "N/A"} />
                                        <SpecItem icon={Car} label="Parking" value={`${property.car_parking_slots || details?.car_parking_slots || details?.carParking || 0} Slots`} />
                                        <SpecItem icon={Home} label="Type" value={property.property_type || details?.property_type || details?.propertyType || "Apartment"} />
                                    </>
                                ) : (
                                    <>
                                        <SpecItem icon={Maximize} label="Plot Area" value={`${details.plot_area || details.plotArea || 0} ${details.plot_area_unit || details.plotAreaUnit || 'sq.ft'}`} />
                                        <SpecItem icon={Compass} label="Facing" value={details.facing_direction || details.facing || "N/A"} />
                                        <SpecItem icon={CheckCircle2} label="DTCP" value={details.dtcp_approved ? "Yes" : "No"} />
                                        <SpecItem icon={CheckCircle2} label="CMDA" value={details.cmda_approved ? "Yes" : "No"} />
                                        <SpecItem icon={Layers} label="Corner" value={details.is_corner_plot ? "Yes" : "No"} />
                                        <SpecItem icon={Building2} label="Gated" value={details.is_gated_community ? "Yes" : "No"} />
                                        <SpecItem icon={CheckCircle2} label="Boundary" value={details.has_boundary_wall ? "Yes" : "No"} />
                                        <SpecItem icon={Home} label="Plot Type" value={details.plot_type || details.plotType || "N/A"} />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-semibold mb-4">Description</h2>
                            <p className="text-gray-600 leading-relaxed">
                                {property.description || details?.description || "No description provided for this property."}
                            </p>
                        </div>

                        {/* Amenities */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-semibold mb-4">Amenities</h2>
                            <div className="flex flex-wrap gap-3">
                                {property.amenities && property.amenities.length > 0 ? (
                                    property.amenities.map((amenity, idx) => (
                                        <span key={idx} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                                            {amenity}
                                        </span>
                                    ))
                                ) : details?.amenities && details.amenities.length > 0 ? (
                                    (typeof details.amenities === 'string' ? JSON.parse(details.amenities) : details.amenities).map((amenity, idx) => (
                                        <span key={idx} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                                            {amenity}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-sm">No specific amenities listed</span>
                                )}
                            </div>
                        </div>

                        {/* Highlights Section - For Housing Properties */}
                        {property.property_category === 'housing' && property.highlights && (
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Sparkles size={20} className="text-yellow-500" />
                                    Property Highlights
                                </h2>
                                <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: property.highlights }}
                                />
                            </div>
                        )}

                        {/* Nearby Locations - For Housing Properties */}
                        {property.property_category === 'housing' && property.nearby_locations && (
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <MapPinned size={20} className="text-green-500" />
                                    Nearby Locations
                                </h2>
                                <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: property.nearby_locations }}
                                />
                            </div>
                        )}

                        {/* Nearby Facilities - For Plot Properties */}
                        {property.property_category !== 'housing' && property.nearby_facilities && property.nearby_facilities.length > 0 && (
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <MapPinned size={20} className="text-green-500" />
                                    Nearby Facilities
                                </h2>
                                <div className="flex flex-wrap gap-3">
                                    {property.nearby_facilities.map((facility, idx) => (
                                        <span key={idx} className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium flex items-center gap-2">
                                            <CheckCircle2 size={14} />
                                            {facility}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Advantages - For Plot Properties */}
                        {property.property_category !== 'housing' && property.advantages && (
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Star size={20} className="text-amber-500" />
                                    Plot Advantages
                                </h2>
                                <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: property.advantages }}
                                />
                            </div>
                        )}

                        {/* Additional Details - For Plot Properties */}
                        {property.property_category !== 'housing' && (
                            (property.survey_number || property.distance_to_main_road || property.soil_type) && (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                        <Info size={20} className="text-blue-500" />
                                        Additional Details
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {property.survey_number && (
                                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                                <FileText size={20} className="text-blue-600 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Survey Number</p>
                                                    <p className="font-semibold text-gray-800">{property.survey_number}</p>
                                                </div>
                                            </div>
                                        )}
                                        {property.distance_to_main_road && (
                                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                                <Route size={20} className="text-orange-600 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Distance to Main Road</p>
                                                    <p className="font-semibold text-gray-800">{property.distance_to_main_road}</p>
                                                </div>
                                            </div>
                                        )}
                                        {property.soil_type && (
                                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                                <TreePine size={20} className="text-green-600 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Soil Type</p>
                                                    <p className="font-semibold text-gray-800">{property.soil_type}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    {/* Sidebar / Contact */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-blue-100 sticky top-24">
                            <div className="flex flex-col items-center text-center mb-6">
                                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-3">
                                    <span className="text-2xl font-bold">AG</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">{property.contact_name || "Agent"}</h3>
                                <p className="text-sm text-gray-500">Real Estate Agent</p>
                            </div>

                            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 mb-3 shadow-lg shadow-blue-500/30 transition-all">
                                <Phone size={18} />
                                View Phone Number
                            </button>
                            <button
                                onClick={() => setShowEnquiryModal(true)}
                                className="w-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                            >
                                <Mail size={18} />
                                Send Message
                            </button>

                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <h4 className="font-semibold text-gray-800 mb-2">Connect via</h4>
                                <div className="text-sm text-gray-600 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span>Preferred Time</span>
                                        <span className="font-medium text-gray-900">{property.contact_time || "Anytime"}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>Posted On</span>
                                        <span className="font-medium text-gray-900">
                                            {new Date(property.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Enquiry Modal */}
            {showEnquiryModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEnquiryModal(false)} />
                    <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className="bg-blue-600 p-6 text-white relative">
                            <button
                                onClick={() => setShowEnquiryModal(false)}
                                className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <MessageCircle className="w-6 h-6" />
                                Send Enquiry
                            </h3>
                            <p className="text-blue-100 text-sm mt-1">Interested in {property.property_title}?</p>
                        </div>

                        <form onSubmit={handleEnquirySubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Full Name *</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    placeholder="Enter your name"
                                    value={enquiryForm.name}
                                    onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Email (Optional)</label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        placeholder="your@email.com"
                                        value={enquiryForm.email}
                                        onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Mobile *</label>
                                    <input
                                        type="tel"
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        placeholder="Phone number"
                                        value={enquiryForm.mobile}
                                        onChange={(e) => setEnquiryForm({ ...enquiryForm, mobile: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Message *</label>
                                <textarea
                                    required
                                    rows="4"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                    placeholder="I am interested in this property..."
                                    value={enquiryForm.message}
                                    onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Message'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Lightbox / Full Screen Gallery */}
            {showLightbox && (
                <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex flex-col">
                    <div className="flex justify-between items-center p-4 md:p-6">
                        <div className="text-white font-medium">
                            {activeImage + 1} / {images.length}
                        </div>
                        <button
                            onClick={() => setShowLightbox(false)}
                            className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-all"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex-grow flex items-center justify-center p-4 relative">
                        {/* Prev Button */}
                        <button
                            onClick={() => setActiveImage((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
                            className="absolute left-4 md:left-8 bg-black/20 hover:bg-black/40 text-white p-3 rounded-full backdrop-blur-md transition-all z-10"
                        >
                            <ArrowLeft size={24} />
                        </button>

                        <img
                            src={images[activeImage].image_url}
                            className="max-w-full max-h-[80vh] object-contain select-none animate-in zoom-in duration-300"
                            alt="Property Visual"
                        />

                        {/* Next Button */}
                        <button
                            onClick={() => setActiveImage((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
                            className="absolute right-4 md:right-8 bg-black/20 hover:bg-black/40 text-white p-3 rounded-full backdrop-blur-md transition-all z-10 rotate-180"
                        >
                            <ArrowLeft size={24} />
                        </button>
                    </div>

                    {/* Bottom Gallery in Lightbox */}
                    <div className="p-6 overflow-x-auto no-scrollbar scrollbar-hide">
                        <div className="flex justify-center gap-3">
                            {images.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img.image_url}
                                    onClick={() => setActiveImage(idx)}
                                    className={`w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg cursor-pointer transition-all duration-300 border-2 ${activeImage === idx ? 'border-blue-400 scale-110 shadow-lg' : 'border-transparent opacity-40 hover:opacity-100'}`}
                                    alt="Thumb"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SpecItem = ({ icon: Icon, label, value }) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Icon size={16} />
            {label}
        </div>
        <div className="font-semibold text-gray-900">{value}</div>
    </div>
);

export default HousingDetail;
